import jsPDF from 'jspdf';
import { BookingsDocument } from '../types';

// Company Details
const COMPANY_DETAILS = {
  name: 'BOZ MEET CONNECT AND TRAVEL PRIVATE LIMITED',
  gstin: '29AALCB5090K1Z5',
  address: 'Hustlehub Tech Park, 36/5, Somasundarapalya Main Rd, adjacent 27th Main Road, ITI Layout, HSR Layout, Bengaluru, Karnataka 560102',
  logo: '/Users/ashrafiqubal/workspace/chat-web/public/boz_icon.png'
};

// Color scheme
const COLORS = {
  primary: '#2563eb', // Blue
  secondary: '#1e40af', // Darker blue
  accent: '#3b82f6', // Light blue
  success: '#059669', // Green
  warning: '#d97706', // Orange
  danger: '#dc2626', // Red
  gray: '#6b7280',
  lightGray: '#f3f4f6',
  darkGray: '#374151'
};

export interface PDFReceiptOptions {
  booking: BookingsDocument;
  includeCompanyDetails?: boolean;
  includeTravellerDetails?: boolean;
  includePaymentBreakdown?: boolean;
}

export class PDFReceiptGenerator {
  private doc: jsPDF;
  private currentY: number = 0;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number = 20;
  private contentWidth: number;

  constructor() {
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - (this.margin * 2);
    this.currentY = this.margin;
  }

  private async loadCompanyLogo(): Promise<string | null> {
    try {
      // Convert the logo to base64 for embedding in PDF
      const response = await fetch('/boz_icon.png');
      if (!response.ok) {
        throw new Error('Logo not found');
      }
      
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.log('Could not load company logo, using text fallback');
      return null;
    }
  }

  private async addHeader(booking: BookingsDocument) {
    // Add background gradient effect
    this.doc.setFillColor(COLORS.lightGray);
    this.doc.rect(0, 0, this.pageWidth, 60, 'F');

    // Company Logo (try to load actual logo)
    try {
      const logoBase64 = await this.loadCompanyLogo();
      
      if (logoBase64) {
        // Add the actual company logo
        this.doc.addImage(logoBase64, 'PNG', this.margin, 15, 20, 20);
        
        // Add company name next to logo
        this.doc.setFontSize(14);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(COLORS.primary);
        this.doc.text('MEET CONNECT AND TRAVEL', this.margin + 25, 25);
        
        // Add decorative line under logo area
        this.doc.setDrawColor(COLORS.primary);
        this.doc.setLineWidth(2);
        this.doc.line(this.margin, 35, this.margin + 80, 35);
        
      } else {
        // Fallback to text-based logo
        this.doc.setFontSize(28);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(COLORS.primary);
        this.doc.text('BOZ', this.margin, 25);
        
        // Add decorative elements around the logo
        this.doc.setDrawColor(COLORS.primary);
        this.doc.setLineWidth(3);
        this.doc.line(this.margin, 30, this.margin + 40, 30);
        
        // Add a small circle accent
        this.doc.setFillColor(COLORS.accent);
        this.doc.circle(this.margin + 45, 28, 3, 'F');
      }
      
    } catch (error) {
      console.log('Logo styling applied');
    }

    // Company Name with better typography
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(COLORS.darkGray);
    this.doc.text(COMPANY_DETAILS.name, this.margin, 40);

    // GSTIN and Address with better formatting
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(COLORS.gray);
    this.doc.text(`GSTIN: ${COMPANY_DETAILS.gstin}`, this.margin, 46);
    this.doc.text(COMPANY_DETAILS.address, this.margin, 52);
    
    this.currentY = 70;

    // Receipt Title with enhanced styling
    this.doc.setFillColor(COLORS.primary);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 15, 3, 3, 'F');
    
    // Add gradient effect simulation with multiple rectangles
    this.doc.setFillColor(COLORS.secondary);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 5, 3, 3, 'F');
    
    this.doc.setFontSize(18);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    this.doc.text('BOOKING RECEIPT', this.margin + 8, this.currentY + 10);
    
    this.currentY += 25;

    // Receipt Number and Date in enhanced styled boxes
    const boxWidth = (this.contentWidth - 15) / 2;
    
    // Receipt Number box with shadow effect
    this.doc.setFillColor(COLORS.lightGray);
    this.doc.roundedRect(this.margin + 2, this.currentY + 2, boxWidth, 12, 3, 3, 'F');
    this.doc.setFillColor(255, 255, 255);
    this.doc.roundedRect(this.margin, this.currentY, boxWidth, 12, 3, 3, 'F');
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(COLORS.primary);
    this.doc.text('Receipt No:', this.margin + 5, this.currentY + 5);
    this.doc.setTextColor(COLORS.darkGray);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(booking.id, this.margin + 5, this.currentY + 9);

    // Date box with shadow effect
    this.doc.setFillColor(COLORS.lightGray);
    this.doc.roundedRect(this.margin + boxWidth + 17, this.currentY + 2, boxWidth, 12, 3, 3, 'F');
    this.doc.setFillColor(255, 255, 255);
    this.doc.roundedRect(this.margin + boxWidth + 15, this.currentY, boxWidth, 12, 3, 3, 'F');
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(COLORS.primary);
    this.doc.text('Date:', this.margin + boxWidth + 20, this.currentY + 5);
    this.doc.setTextColor(COLORS.darkGray);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(new Date(booking.createdOn).toLocaleDateString('en-IN'), this.margin + boxWidth + 20, this.currentY + 9);
    
    this.currentY += 20;
  }

  private addBookingDetails(booking: BookingsDocument) {
    // Section Header with background
    this.doc.setFillColor(COLORS.accent);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 8, 2, 2, 'F');
    
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    this.doc.text('TRIP DETAILS', this.margin + 5, this.currentY + 6);
    this.currentY += 15;

    // Create a styled info box
    this.doc.setFillColor(COLORS.lightGray);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 35, 3, 3, 'F');
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(COLORS.darkGray);
    
    let yOffset = 5;
    
    // Event Name
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Event:', this.margin + 5, this.currentY + yOffset);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(booking.eventName, this.margin + 20, this.currentY + yOffset);
    yOffset += 6;

    // Trip Date
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Date:', this.margin + 5, this.currentY + yOffset);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(booking.tripDate, this.margin + 20, this.currentY + yOffset);
    yOffset += 6;

    // Start Time (if available)
    if (booking.startTime) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Time:', this.margin + 5, this.currentY + yOffset);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(new Date(booking.startTime).toLocaleTimeString('en-IN'), this.margin + 20, this.currentY + yOffset);
      yOffset += 6;
    }

    // Number of Travellers
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Travellers:', this.margin + 5, this.currentY + yOffset);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`${booking.count} ${booking.count === 1 ? 'Person' : 'People'}`, this.margin + 20, this.currentY + yOffset);
    yOffset += 6;

    // Status with color coding
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Status:', this.margin + 5, this.currentY + yOffset);
    this.doc.setFont('helvetica', 'normal');
    
    // Color code status
    const statusColor = this.getStatusColor(booking.status);
    this.doc.setTextColor(statusColor);
    this.doc.text(booking.status.toUpperCase(), this.margin + 20, this.currentY + yOffset);
    
    this.currentY += 45;
  }

  private formatCurrency(amount: number): string {
    // Clean currency formatting without special characters
    const formattedAmount = Math.abs(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    const prefix = amount < 0 ? '-₹' : '₹';
    return prefix + formattedAmount;
  }

  private getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'paid':
        return COLORS.success;
      case 'partiallypaid':
        return COLORS.warning;
      case 'failed':
        return COLORS.danger;
      case 'init':
        return COLORS.primary;
      default:
        return COLORS.gray;
    }
  }

  private addCustomerDetails(booking: BookingsDocument) {
    // Section Header
    this.doc.setFillColor(COLORS.accent);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 8, 2, 2, 'F');
    
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    this.doc.text('CUSTOMER DETAILS', this.margin + 5, this.currentY + 6);
    this.currentY += 15;

    // Customer info box
    this.doc.setFillColor(COLORS.lightGray);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 25, 3, 3, 'F');
    
    this.doc.setFontSize(10);
    this.doc.setTextColor(COLORS.darkGray);
    
    let yOffset = 5;
    
    // Name
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Name:', this.margin + 5, this.currentY + yOffset);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(booking.userName, this.margin + 20, this.currentY + yOffset);
    yOffset += 6;

    // Phone
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Phone:', this.margin + 5, this.currentY + yOffset);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(booking.phoneNumber, this.margin + 20, this.currentY + yOffset);
    yOffset += 6;

    // Email (if available)
    if (booking.email) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Email:', this.margin + 5, this.currentY + yOffset);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(booking.email, this.margin + 20, this.currentY + yOffset);
      yOffset += 6;
    }

    this.currentY += 35;
  }

  private addTravellerDetails(booking: BookingsDocument) {
    if (!booking.travellers || booking.travellers.length === 0) return;

    // Section Header
    this.doc.setFillColor(COLORS.accent);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 8, 2, 2, 'F');
    
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(`TRAVELLER DETAILS (${booking.travellers.length})`, this.margin + 5, this.currentY + 6);
    this.currentY += 15;

    // Table headers with styling
    const colWidths = [35, 15, 30, 40, 25, 15];
    const headers = ['Name', 'Gender', 'Phone', 'Email', 'Location', 'Primary'];
    let xPos = this.margin;

    // Header background
    this.doc.setFillColor(COLORS.primary);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 8, 2, 2, 'F');
    
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    
    headers.forEach((header, index) => {
      this.doc.text(header, xPos + 2, this.currentY + 6);
      xPos += colWidths[index];
    });
    this.currentY += 12;

    // Traveller rows with alternating colors
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    
    booking.travellers.forEach((traveller, index) => {
      if (this.currentY > this.pageHeight - 40) {
        this.doc.addPage();
        this.currentY = this.margin;
      }

      // Alternate row colors
      const isEven = index % 2 === 0;
      if (isEven) {
        this.doc.setFillColor(COLORS.lightGray);
      } else {
        this.doc.setFillColor(255, 255, 255);
      }
      this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 8, 1, 1, 'F');

      xPos = this.margin;
      const rowData = [
        traveller.name.substring(0, 15), // Truncate long names
        traveller.gender === 1 ? 'Male' : traveller.gender === 2 ? 'Female' : 'Other',
        traveller.phoneNumber.substring(0, 12),
        traveller.email ? traveller.email.substring(0, 18) : '-',
        traveller.tripLocation?.displayText?.substring(0, 12) || '-',
        traveller.primaryUser ? 'Yes' : 'No'
      ];

      this.doc.setTextColor(COLORS.darkGray);
      rowData.forEach((data, colIndex) => {
        this.doc.text(data, xPos + 2, this.currentY + 6);
        xPos += colWidths[colIndex];
      });
      this.currentY += 10;
    });

    this.currentY += 10;
  }

  private addPaymentDetails(booking: BookingsDocument) {
    // Section Header
    this.doc.setFillColor(COLORS.accent);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 8, 2, 2, 'F');
    
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    this.doc.text('PAYMENT DETAILS', this.margin + 5, this.currentY + 6);
    this.currentY += 15;

    // Payment breakdown table
    const paymentData = [
      { label: 'Actual Amount', amount: booking.actualAmount, color: COLORS.darkGray },
      { label: 'Ticket Amount', amount: booking.ticketAmount || 0, color: COLORS.darkGray },
      { label: 'Convenience Fee', amount: booking.convenienceFee, color: COLORS.darkGray },
    ];

    if (booking.gstAmount && booking.gstAmount > 0) {
      paymentData.push({ label: 'GST Amount', amount: booking.gstAmount, color: COLORS.darkGray });
    }

    if (booking.totalDiscount && booking.totalDiscount > 0) {
      paymentData.push({ label: 'Discount', amount: -booking.totalDiscount, color: COLORS.success });
    }

    paymentData.push({ label: 'Order Amount', amount: booking.orderAmount, color: COLORS.primary });
    paymentData.push({ label: 'Amount Paid', amount: booking.amountPaid, color: COLORS.success });
    
    if (booking.amountRemaining && booking.amountRemaining > 0) {
      paymentData.push({ label: 'Amount Remaining', amount: booking.amountRemaining, color: COLORS.warning });
    }

    // Create payment table with styling
    this.doc.setFillColor(COLORS.lightGray);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, paymentData.length * 8 + 10, 3, 3, 'F');

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(COLORS.darkGray);
    
    paymentData.forEach((item, index) => {
      const yPos = this.currentY + 5 + (index * 8);
      
      // Label
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(COLORS.darkGray);
      this.doc.text(item.label + ':', this.margin + 5, yPos);
      
      // Amount with clean formatting
      this.doc.setTextColor(item.color);
      this.doc.setFont('helvetica', 'normal');
      
      // Use the clean currency formatter
      const amountText = this.formatCurrency(item.amount);
      this.doc.text(amountText, this.pageWidth - this.margin - 50, yPos);
      
      // Reset color for next iteration
      this.doc.setTextColor(COLORS.darkGray);
    });

    this.currentY += paymentData.length * 8 + 20;
  }

  private async addFooter() {
    // Decorative line
    this.doc.setDrawColor(COLORS.primary);
    this.doc.setLineWidth(2);
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
    this.currentY += 15;

    // Terms and conditions with styled background
    this.doc.setFillColor(COLORS.lightGray);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 35, 3, 3, 'F');
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(COLORS.primary);
    this.doc.text('Terms & Conditions:', this.margin + 5, this.currentY + 8);
    this.currentY += 12;

    const terms = [
      '1. This receipt is valid for the trip mentioned above.',
      '2. Please carry a valid ID proof during the trip.',
      '3. Cancellation policy as per company terms.',
      '4. For any queries, contact us at the above address.',
      '5. GST is applicable as per government regulations.'
    ];

    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(COLORS.darkGray);
    
    terms.forEach(term => {
      this.doc.text(term, this.margin + 5, this.currentY);
      this.currentY += 4;
    });

    this.currentY += 10;

    // Thank you message with styling
    this.doc.setFillColor(COLORS.primary);
    this.doc.roundedRect(this.margin, this.currentY, this.contentWidth, 12, 2, 2, 'F');
    
    // Try to add small logo next to thank you message
    try {
      const logoBase64 = await this.loadCompanyLogo();
      if (logoBase64) {
        // Add small logo next to thank you text
        this.doc.addImage(logoBase64, 'PNG', this.margin + 5, this.currentY + 2, 8, 8);
      }
    } catch (error) {
      // Ignore logo errors
    }
    
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    this.doc.text('Thank you for choosing BOZ!', this.margin + 15, this.currentY + 8);
    this.currentY += 20;

    // Support information
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(COLORS.gray);
    this.doc.text('For support: Contact us at the above address or phone number', this.margin, this.currentY);
    this.currentY += 5;
    
    // Company website/contact
    this.doc.text('Visit us at: www.bozapp.in | Email: support@bozapp.in', this.margin, this.currentY);
  }

  private async addPageNumbers() {
    const pageCount = this.doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i);
      
      // Add a subtle footer line
      this.doc.setDrawColor(COLORS.lightGray);
      this.doc.setLineWidth(0.5);
      this.doc.line(this.margin, this.pageHeight - 15, this.pageWidth - this.margin, this.pageHeight - 15);
      
      // Try to add small logo in footer
      try {
        const logoBase64 = await this.loadCompanyLogo();
        if (logoBase64) {
          // Add small logo in footer
          this.doc.addImage(logoBase64, 'PNG', this.margin, this.pageHeight - 12, 8, 8);
        }
      } catch (error) {
        // Ignore logo errors in footer
      }
      
      // Page number with styling
      this.doc.setFontSize(8);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(COLORS.gray);
      this.doc.text(`Page ${i} of ${pageCount}`, this.pageWidth - 30, this.pageHeight - 10);
      
      // Add company name in footer
      this.doc.text('BOZ MEET CONNECT AND TRAVEL PRIVATE LIMITED', this.margin + 10, this.pageHeight - 10);
    }
  }

  public async generateReceipt(options: PDFReceiptOptions): Promise<jsPDF> {
    const { booking } = options;
    
    // Reset for new PDF
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - (this.margin * 2);
    this.currentY = this.margin;

    // Add all sections
    await this.addHeader(booking);
    this.addBookingDetails(booking);
    this.addCustomerDetails(booking);
    
    if (options.includeTravellerDetails !== false) {
      this.addTravellerDetails(booking);
    }
    
    if (options.includePaymentBreakdown !== false) {
      this.addPaymentDetails(booking);
    }
    
    await this.addFooter();
    await this.addPageNumbers();

    return this.doc;
  }

  public async getReceiptAsBlob(options: PDFReceiptOptions): Promise<Blob> {
    const doc = await this.generateReceipt(options);
    return doc.output('blob');
  }

  public async downloadReceipt(options: PDFReceiptOptions, filename?: string): Promise<void> {
    const doc = await this.generateReceipt(options);
    const defaultFilename = `BOZ_Receipt_${options.booking.id}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename || defaultFilename);
  }
}

// Utility function for easy use
export const generateBookingReceipt = async (booking: BookingsDocument, options?: Partial<PDFReceiptOptions>): Promise<void> => {
  const generator = new PDFReceiptGenerator();
  const defaultOptions: PDFReceiptOptions = {
    booking,
    includeCompanyDetails: true,
    includeTravellerDetails: true,
    includePaymentBreakdown: true,
    ...options
  };
  
  await generator.downloadReceipt(defaultOptions);
};

// Utility function to send receipt as message
export const sendBookingReceipt = async (
  userId: number, 
  booking: BookingsDocument, 
  uploadMedia: (file: File, onProgress?: (progress: number) => void) => Promise<{ url: string }>,
  sendMessage: (userId: number, messageData: { message: string; type: string }) => Promise<any>,
  options?: Partial<PDFReceiptOptions>
): Promise<void> => {
  const generator = new PDFReceiptGenerator();
  const defaultOptions: PDFReceiptOptions = {
    booking,
    includeCompanyDetails: true,
    includeTravellerDetails: true,
    includePaymentBreakdown: true,
    ...options
  };

  // Generate PDF as Blob
  const pdfBlob = await generator.getReceiptAsBlob(defaultOptions);
  
  // Convert Blob to File
  const fileName = `BOZ_Receipt_${booking.id}_${new Date().toISOString().split('T')[0]}.pdf`;
  const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

  // Upload PDF file
  const uploadResult = await uploadMedia(pdfFile);

  // Send message with PDF URL
  await sendMessage(userId, {
    message: uploadResult.url,
    type: 'Document' // or 'File', 'PDF' - adjust based on your API
  });
};

export default PDFReceiptGenerator;
